import React, { useState } from 'react';
import { CalendarClock, Key, ShieldAlert, ShieldCheck } from 'lucide-react';
import api, { errorText } from '../services/api';
import { useApi } from '../lib/useApi';
import { useAccess } from '../context/AuthContext';
import { date } from '../lib/format';
import { Button, Card, Chip, Field, Input, Modal, PageHeader, PageSkeleton, ErrorBanner, Tone, useToast } from '../components/ui';

interface Credential {
  credential_key: string;
  label: string;
  description: string;
  configured: boolean;
  value_masked: string | null;
  expires_at: string | null;
  updated_at: string | null;
  updated_by_admin_email: string | null;
}

const WARN_WITHIN_DAYS = 14;

function daysUntil(dateStr: string): number {
  const ms = new Date(dateStr + 'T00:00:00').getTime() - new Date(new Date().toDateString()).getTime();
  return Math.round(ms / 86400000);
}

function status(c: Credential): { label: string; tone: Tone; urgent: boolean } {
  if (!c.configured) return { label: 'Not set', tone: 'slate', urgent: true };
  if (!c.expires_at) return { label: 'No expiry set', tone: 'amber', urgent: false };
  const d = daysUntil(c.expires_at);
  if (d < 0) return { label: `Expired ${-d}d ago`, tone: 'red', urgent: true };
  if (d <= WARN_WITHIN_DAYS) return { label: `Expires in ${d}d`, tone: 'amber', urgent: true };
  return { label: `Expires in ${d}d`, tone: 'green', urgent: false };
}

/** Credentials shared by every tenant's scans (not per-tenant -- see FluxWatch's own
    database_pg/platform_credentials.py). First one: the GitHub token the code/secret-leak checks
    use, which used to be a bare GITHUB_TOKEN env var nobody tracked for expiry. */
export default function PlatformCredentials() {
  const toast = useToast();
  const { canWrite } = useAccess();
  const { data, error, loading, reload } = useApi<{ credentials: Credential[] }>('/platform-credentials');
  const [editing, setEditing] = useState<Credential | null>(null);

  if (loading) return <PageSkeleton />;
  if (error) return <ErrorBanner text={error} onRetry={reload} />;
  const credentials = data!.credentials;
  const urgentCount = credentials.filter(c => status(c).urgent).length;

  return (
    <div>
      <PageHeader title="Platform credentials" subtitle="Shared credentials every tenant's scans use -- configured once here, not per tenant. Review the expiry date before it lapses; a lapsed key just makes the checks that use it report 'not configured', nothing breaks." />

      {urgentCount > 0 && (
        <div className="mb-5 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <ShieldAlert className="h-4 w-4 shrink-0" />
          {urgentCount} credential{urgentCount === 1 ? '' : 's'} need{urgentCount === 1 ? 's' : ''} attention (missing, expired, or expiring within {WARN_WITHIN_DAYS} days).
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        {credentials.map(c => {
          const s = status(c);
          return (
            <Card key={c.credential_key} title={c.label} icon={s.urgent ? ShieldAlert : ShieldCheck} tone={s.urgent ? 'amber' : 'green'}
              right={<Chip tone={s.tone} dot>{s.label}</Chip>}>
              <p className="mb-3 text-sm text-slate-600">{c.description}</p>
              <dl className="mb-4 space-y-1.5 text-sm">
                <div className="flex justify-between"><dt className="text-slate-500">Value</dt>
                  <dd className="font-mono text-slate-900">{c.value_masked || '—'}</dd></div>
                <div className="flex justify-between"><dt className="text-slate-500">Expires</dt>
                  <dd className="text-slate-900">{c.expires_at ? date(c.expires_at) : '—'}</dd></div>
                <div className="flex justify-between"><dt className="text-slate-500">Last updated</dt>
                  <dd className="text-slate-900">{c.updated_at ? `${date(c.updated_at)}${c.updated_by_admin_email ? ` · ${c.updated_by_admin_email}` : ''}` : '—'}</dd></div>
              </dl>
              {canWrite && (
                <Button icon={Key} onClick={() => setEditing(c)}>{c.configured ? 'Update' : 'Set'} value</Button>
              )}
            </Card>
          );
        })}
      </div>

      {editing && <EditModal credential={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}
    </div>
  );
}

function EditModal({ credential, onClose, onSaved }: { credential: Credential; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const [value, setValue] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    if (!value.trim()) { setErr('Value is required'); return; }
    setBusy(true);
    setErr(null);
    try {
      await api.post(`/platform-credentials/${credential.credential_key}`, { value: value.trim(), expires_at: expiresAt || null });
      toast('ok', `${credential.label} updated`);
      onSaved();
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open title={`${credential.configured ? 'Update' : 'Set'} ${credential.label}`} onClose={onClose}
      footer={<><Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="primary" loading={busy} onClick={save}>Save</Button></>}>
      <div className="space-y-4">
        <Field label="Value" hint="Tested against GitHub before saving -- a bad or expired token is never stored.">
          <Input type="password" autoComplete="off" value={value} onChange={e => setValue(e.target.value)} placeholder="ghp_..." autoFocus />
        </Field>
        <Field label="Expiry date" hint={<span className="flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5" /> Whatever expiry you picked when generating this token on GitHub's own side -- GitHub doesn't expose it back to us via the API.</span>}>
          <Input type="date" value={expiresAt} onChange={e => setExpiresAt(e.target.value)} />
        </Field>
        {err && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}
      </div>
    </Modal>
  );
}
